/**
 * NgRx Runtime Checks
 *
 * These checks help detect common mistakes during development such as
 * mutating the state or actions. They should only be enabled in development
 * because they add extra runtime overhead.
 *
 * NOTE: Our application runs in a zoneless Angular setup and uses
 * signals/selectSignal, so some checks are intentionally disabled.
 */
export const STORE_RUNTIME_CHECKS = {
  /**
   * Ensures that the state object is never mutated directly.
   * NgRx requires immutable updates so reducers must always
   * return a new state object.
   *
   * Example of forbidden mutation:
   * state.user.name = 'John'
   *
   * Correct approach:
   * return { ...state, user: { ...state.user, name: 'John' } }
   */
  strictStateImmutability: true,

  /**
   * Ensures actions are not mutated after they are dispatched.
   * Actions should be treated as immutable events.
   */
  strictActionImmutability: true,

  /**
   * Verifies that the store state is serializable.
   *
   * Disabled because real-world applications often store values like:
   * - Date objects
   * - complex API responses
   * - router objects
   *
   * Enforcing strict serialization can create unnecessary friction.
   */
  strictStateSerializability: false,

  /**
   * Verifies that dispatched actions are serializable.
   *
   * Disabled because some actions may carry non-serializable payloads
   * (e.g. File, Blob, FormData) when interacting with APIs.
   */
  strictActionSerializability: false,

  /**
   * Ensures actions are dispatched inside Angular's NgZone.
   *
   * Disabled because this application runs in a **zoneless Angular setup**,
   * so NgZone is not used for change detection.
   */
  strictActionWithinNgZone: false,
};
